"use client";
import { useState, useEffect, useRef } from "react";
import styles from "./index.module.css";

import SearchBar from "./searchbar";

const CoverPage = ({
  organisation,
  searchTerm,
  activateSearch,
  handleSearchTerm,
  searchBarRef = null,
}) => {
  const [fontColor, setFontColor] = useState("black"); // default
  const [opacityClass, setOpacityClass] = useState("noOpacity");
  const [imageError, setImageError] = useState(false); // 👈 track if image fails
  const imageRef = useRef(null);

  const getLuminance = (r, g, b) => {
    const [red, green, blue] = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  };

  const determineFontColor = () => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = imageRef.current;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let r = 0,
        g = 0,
        b = 0,
        count = 0;
      for (let i = 0; i < data.length; i += 4 * 10) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }
      r = Math.round(r / count);
      g = Math.round(g / count);
      b = Math.round(b / count);

      const luminance = getLuminance(r, g, b);
      const isBright = luminance > 0.5;
      setFontColor(isBright ? "black" : "white");
      setOpacityClass(isBright ? "whiteOpacity" : "blackOpacity");
    } catch (err) {
      setFontColor("black");
      setOpacityClass("whiteOpacity");
    }
  };

  useEffect(() => {
    if (!organisation?.coverPhoto) return;
    const img = imageRef.current;
    if (!img) return;

    const handleLoad = () => determineFontColor();
    const handleError = () => setImageError(true);

    if (img.complete) {
      if (img.naturalWidth === 0) {
        setImageError(true);
      } else {
        handleLoad();
      }
    } else {
      img.addEventListener("load", handleLoad);
      img.addEventListener("error", handleError);
    }

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [organisation?.coverPhoto]);

  const showFallback = imageError || !organisation?.coverPhoto;

  return (
    <div  className={styles.heroPad} >
      <div
        className={styles.hero}
        style={
          !showFallback
            ? {
                "--custom-background": `url(${organisation?.coverPhoto.replace(
                  "r200x200",
                  "r1500x1500",
                )})`,
              }
            : {
                backgroundColor: "#f5f5f5", // 👈 fallback background
              }
        }
      >
        {!showFallback && (
          <img
            ref={imageRef}
            src={organisation?.coverPhoto}
            alt={organisation?.orgName || "Cover image"}
            style={{ display: "none" }}
            crossOrigin="anonymous"
          />
        )}

        <div className={`${opacityClass} ${styles.hideMobile}`}>
          <div className={styles.title} style={{ color: fontColor }}>
            {organisation?.orgName || "Unnamed Organisation"}
          </div>
          <div className={styles.description} style={{ color: fontColor }}>
            {organisation?.description || "No description available"}
          </div>
        </div>

        <SearchBar
          searchTerm={searchTerm}
          activateSearch={activateSearch}
          handleSearchTerm={handleSearchTerm}
          searchBarRef={searchBarRef}
        />
      </div>
    </div>
  );
};

export default CoverPage;
